import * as React from 'react';
import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useSnackbar} from 'notistack';

import {ERROR_STRUCTURE, ERROR_FAVORITES} from '../constants';
import {useAuth} from './auth';
import {useSearch} from './search';
import {getStructure, hasFavorites} from '../api';
import {
  StructureResponseItemDto,
  AuthorizationLevel,
  StructureResponseDto,
  StructureTreeViewItem,
  FAVORITE_STRUCTURE_TREE_ITEM,
  MEDIENEINGANG_STRUCTURE_RESULT_ITEM,
} from '../models';
import {isPresent} from '../utils/utils';

interface StructureContextValue {
  structure: StructureTreeViewItem[];
  reloadStructure: () => void;
  resetStructure: () => void;
  structureFavorites: StructureTreeViewItem[];
  selectStructureItem: (itemId: string, search?: boolean) => void;
  checkHasAssetFavorites: () => void;
  expandedItems: string[];
  setExpandedItems: (value: string[]) => void;
  selectedItemId: string | null;
  selectedItemName: string | null;
}

export const StructureContext = React.createContext<StructureContextValue>({
  structure: [],
  reloadStructure: () => {},
  resetStructure: () => {},
  structureFavorites: [],
  selectStructureItem: () => {},
  checkHasAssetFavorites: () => {},
  expandedItems: [],
  setExpandedItems: () => {},
  selectedItemId: null,
  selectedItemName: null,
});

export const useStructure = () => useContext(StructureContext);

export const StructureProvider: React.FC<PropsWithChildren> = ({children}) => {
  const [structure, setStructure] = useState<StructureTreeViewItem[]>([]);
  const [hasAssetFavorites, setHasAssetFavorites] = useState<boolean>(false);
  const {token, forceLogout, authorizations} = useAuth();
  const {enqueueSnackbar} = useSnackbar();
  const {abortSearch, changeValues, structureId} = useSearch();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const resetStructure = useCallback(() => {
    setExpandedItems([]);
    setSelectedItemId(null);
  }, []);

  function buildTreeItems(
    items: StructureResponseItemDto[],
  ): StructureTreeViewItem[] {
    const itemMap = new Map<string, StructureTreeViewItem>();
    items.forEach(item =>
      itemMap.set(item.id, {
        id: item.id,
        label: item.name,
        children: [],
        parent: undefined,
        details: item,
      }),
    );
    itemMap.values().forEach(item => {
      if (item.details.parentId) {
        const parent = itemMap.get(item.details.parentId);
        parent?.children?.push(item);
        item.parent = parent;
      }
    });
    const treeItems: StructureTreeViewItem[] = [];
    itemMap.values().forEach(item => {
      if (item.details.topLevel) {
        treeItems.push(item);
      }
    });
    return treeItems;
  }

  const checkHasAssetFavorites = useCallback(() => {
    if (!token) {
      setHasAssetFavorites(false);
      return;
    }

    hasFavorites(token)
      .then(response => {
        setHasAssetFavorites(response.data.hasFavorites);
      })
      .catch(err => {
        setHasAssetFavorites(false);
        switch (err?.response?.status) {
          case 401:
            forceLogout();
            break;
          default:
            enqueueSnackbar(ERROR_FAVORITES, {variant: 'error'});
        }
      });
  }, [enqueueSnackbar, token, forceLogout]);

  const retrieveStructure = useCallback(
    (token: string, logout, enqueueSnackbar) => {
      let data: StructureResponseDto;

      getStructure(token)
        .then(res => {
          if (res.data.items?.length > 0) {
            data = res.data;
            data.items.sort((a, b) =>
              a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
            );
            if (authorizations?.ASSETS.includes(AuthorizationLevel.CREATE)) {
              data.items.push(MEDIENEINGANG_STRUCTURE_RESULT_ITEM);
            }
            const treeItems = buildTreeItems(data.items);
            setStructure(treeItems);
          } else {
            setStructure([]);
            //enqueueSnackbar(NO_RESULT_STRUCTURE, { variant: "warning" });
          }
        })
        .catch(err => {
          switch (err?.response?.status) {
            case 401:
              logout();
              break;
            default:
              enqueueSnackbar(ERROR_STRUCTURE, {variant: 'error'});
          }
        });

      checkHasAssetFavorites();
    },
    [authorizations, checkHasAssetFavorites],
  );

  const flatten = useCallback(
    (items: StructureTreeViewItem[]): StructureTreeViewItem[] =>
      items.flatMap(item => [item, ...flatten(item.children || [])]),
    [],
  );

  const structureMap = useMemo(() => {
    const map = new Map<string, StructureTreeViewItem>();
    flatten(structure).forEach(item => {
      map.set(item.id, item);
    });
    return map;
  }, [structure, flatten]);

  const structureFavorites: StructureTreeViewItem[] = useMemo(() => {
    const structureTreeViewItems = Array.from(structureMap.values());
    if (hasAssetFavorites) {
      structureTreeViewItems.unshift(FAVORITE_STRUCTURE_TREE_ITEM);
    }
    return structureTreeViewItems
      .filter(item => item.details.favorite)
      .map(item => {
        const favoriteItem = {...item};
        favoriteItem.children = [];
        return favoriteItem;
      });
  }, [structureMap, hasAssetFavorites]);

  // Load initial structure once on mount
  useEffect(() => {
    retrieveStructure(token ?? '', forceLogout, enqueueSnackbar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadStructure = useCallback(() => {
    retrieveStructure(token ?? '', forceLogout, enqueueSnackbar);
  }, [token, forceLogout, enqueueSnackbar, retrieveStructure]);

  const selectStructureItem = useCallback(
    (itemId: string, search: boolean = true) => {
      let structureItem = structureMap.get(itemId);
      if (itemId === FAVORITE_STRUCTURE_TREE_ITEM.id) {
        structureItem = FAVORITE_STRUCTURE_TREE_ITEM;
      }

      if (structureItem) {
        if (search) {
          abortSearch();
          changeValues({
            structureId: itemId,
            text: '',
          });
        }

        const parentIds: string[] = [];
        let parent = structureItem.parent;
        // Traverse parents; Stop on repeating parent
        while (isPresent(parent) && !parentIds.some(id => id === parent?.id)) {
          parentIds.push(parent.id);
          parent = parent.parent;
        }

        setExpandedItems(prev => [...prev, ...parentIds]);
        setSelectedItemId(structureItem.id);
      } else {
        setSelectedItemId(null);
      }
    },
    [structureMap, abortSearch, changeValues],
  );

  // Lade ausgewählte Strukturgruppe vom Suchkontext
  useEffect(() => {
    setSelectedItemId(prev => {
      if (prev === structureId) {
        return prev;
      }
      if (isPresent(structureId) && structureId.length > 0) {
        return structureId;
      }
      return null;
    });
  }, [structureId]);

  const selectedItemName = useMemo(() => {
    if (isPresent(selectedItemId)) {
      return structureMap.get(selectedItemId)?.details.name ?? null;
    } else {
      return null;
    }
  }, [structureMap, selectedItemId]);

  const value = useMemo(
    () => ({
      structure,
      reloadStructure,
      resetStructure,
      structureFavorites,
      selectStructureItem,
      checkHasAssetFavorites,
      expandedItems,
      setExpandedItems,
      selectedItemId,
      selectedItemName,
    }),
    [
      structure,
      reloadStructure,
      resetStructure,
      structureFavorites,
      selectStructureItem,
      checkHasAssetFavorites,
      expandedItems,
      setExpandedItems,
      selectedItemId,
      selectedItemName,
    ],
  );
  return (
    <StructureContext.Provider value={value}>
      {children}
    </StructureContext.Provider>
  );
};
